// Core
import React, { useMemo } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Components
import {
  LineChart,
  LineChartType,
  SeriesColorEnum,
} from '../../../../../../layout/elements/LineChart';

// View
import { RingBounceProps } from './RingBounce.types';
import { ChartMode } from '../Trendings.types';

// Constants
const DEFAULT_TRANSLATE_LEGEND: string = 'view.crusher.trendings.ringBounceSensor';

function RingBounce({
  className,
  chartMode,
  testId,
  trendingRingBounceSensorData,
  unitData,
}: RingBounceProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  // Generate dynamic ID based on enabled sensors and chart mode to force chart remount
  const chartId = useMemo((): string => {
    const sensorsState: string = [
      unitData?.vibrationSensor1Enable || false,
      unitData?.vibrationSensor2Enable || false,
      unitData?.vibrationSensor3Enable || false,
      unitData?.vibrationSensor4Enable || false,
    ].map(Boolean).join('');

    const baseId: string = `ring-bounce-${chartMode}`;
    return `${baseId}-${sensorsState}`;
  }, [
    chartMode,
    unitData?.vibrationSensor1Enable,
    unitData?.vibrationSensor2Enable,
    unitData?.vibrationSensor3Enable,
    unitData?.vibrationSensor4Enable,
  ]);

  const series = useMemo(() => [
    {
      name: t('common.cloudConnection'),
      field: 'edgeStatus',
      type: LineChartType.fill,
      date: 'timestamp',
      opposite: true,
    },
    ...(unitData?.vibrationSensor1Enable ? [{
      name: t(`${DEFAULT_TRANSLATE_LEGEND}.legend.vibrationSensor1`),
      field: 'vibrationSensor1',
      type: LineChartType.line,
      date: 'timestamp',
      color: SeriesColorEnum.lightPink,
      unit: '%',
      bullet: true,
      decimals: 2,
      minScale: 0,
      maxScale: 100,
    }] : []),
    ...(unitData?.vibrationSensor2Enable ? [{
      name: t(`${DEFAULT_TRANSLATE_LEGEND}.legend.vibrationSensor2`),
      field: 'vibrationSensor2',
      type: LineChartType.line,
      date: 'timestamp',
      color: SeriesColorEnum.lightGreen,
      unit: '%',
      bullet: true,
      decimals: 2,
      minScale: 0,
      maxScale: 100,
    }] : []),
    ...(unitData?.vibrationSensor3Enable ? [{
      name: t(`${DEFAULT_TRANSLATE_LEGEND}.legend.vibrationSensor3`),
      field: 'vibrationSensor3',
      type: LineChartType.line,
      date: 'timestamp',
      color: SeriesColorEnum.blue,
      unit: '%',
      bullet: true,
      decimals: 2,
      minScale: 0,
      maxScale: 100,
    }] : []),
    ...(unitData?.vibrationSensor4Enable ? [{
      name: t(`${DEFAULT_TRANSLATE_LEGEND}.legend.vibrationSensor4`),
      field: 'vibrationSensor4',
      type: LineChartType.line,
      date: 'timestamp',
      color: SeriesColorEnum.orange,
      unit: '%',
      bullet: true,
      decimals: 2,
      minScale: 0,
      maxScale: 100,
    }] : []),
    // {
    //   name: t(`${DEFAULT_TRANSLATE_LEGEND}.legend.maxVibration`),
    //   field: 'maxVibration',
    //   type: LineChartType.line,
    //   date: 'timestamp',
    //   color: 'orange',
    //   unit: '%',
    // },
  ], [
    t,
    unitData?.vibrationSensor1Enable,
    unitData?.vibrationSensor2Enable,
    unitData?.vibrationSensor3Enable,
    unitData?.vibrationSensor4Enable,
  ]);

  return (
    <LineChart
      activeBullet={chartMode === ChartMode.live || chartMode === ChartMode.liveAggregate}
      className={className}
      data={trendingRingBounceSensorData}
      id={chartId}
      series={series}
      testId={testId}
    />
  );
}

export { RingBounce };
